import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, HeartHandshake, Languages } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAuth, homeFor } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { NotificationBell } from "@/components/NotificationBell";
import { EmergencyButton } from "@/components/EmergencyButton";

export const Logo = () => (
  <Link to="/" data-testid="nav-home-link" className="flex items-center gap-2">
    <span className="grid place-items-center w-9 h-9 rounded-xl bg-[#0D5C46] text-white"><HeartHandshake size={20} /></span>
    <span className="font-display text-2xl font-bold tracking-tight">Rent<span className="text-[#0D5C46]">My</span>Date</span>
  </Link>
);

const LangToggle = () => {
  const { lang, setLang } = useLang();
  return (
    <button data-testid="language-toggle-btn" onClick={() => setLang(lang === "en" ? "hi" : "en")}
      className="flex items-center gap-1.5 rounded-full border border-slate-300 px-3 py-1.5 text-sm font-semibold hover:border-[#0D5C46] transition-colors">
      <Languages size={15} />{lang === "en" ? "हिंदी" : "English"}
    </button>
  );
};

const NavLinks = ({ onClick }) => {
  const { t } = useLang();
  const cls = ({ isActive }) => `text-sm font-semibold transition-colors ${isActive ? "text-[#0D5C46]" : "text-slate-700 hover:text-[#0D5C46]"}`;
  return (
    <>
      <NavLink to="/companions" className={cls} onClick={onClick} data-testid="nav-find-link">{t("nav_find")}</NavLink>
      <NavLink to="/safety" className={cls} onClick={onClick} data-testid="nav-safety-link">{t("nav_safety")}</NavLink>
      <NavLink to="/become-companion" className={cls} onClick={onClick} data-testid="nav-become-link">{t("nav_become")}</NavLink>
      <NavLink to="/support" className={cls} onClick={onClick} data-testid="nav-support-link">{t("nav_support")}</NavLink>
    </>
  );
};

const AuthButtons = ({ onClick }) => {
  const { user, signOut } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  if (!user)
    return (
      <>
        <Link to="/login" onClick={onClick} data-testid="nav-login-btn" className="text-sm font-semibold px-3 py-2">{t("login")}</Link>
        <Button asChild className="rounded-full" data-testid="nav-signup-btn"><Link to="/register" onClick={onClick}>{t("signup")}</Link></Button>
      </>
    );
  const label = user.role === "companion" ? t("my_companion") : user.role === "customer" ? t("dashboard") : t("admin");
  return (
    <>
      <Button asChild variant="outline" className="rounded-full" data-testid="nav-dashboard-btn"><Link to={homeFor(user)} onClick={onClick}>{label}</Link></Button>
      <button data-testid="nav-logout-btn" className="text-sm font-semibold text-slate-600 hover:text-rose-600 px-2" onClick={() => { signOut(); onClick?.(); nav("/"); }}>{t("logout")}</button>
    </>
  );
};

export const Header = () => {
  const { user } = useAuth();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/80">
      <div className="container-x h-16 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-7"><NavLinks /></nav>
        <div className="flex items-center gap-2">
          <EmergencyButton compact />
          <div className="hidden sm:block"><LangToggle /></div>
          {user && <NotificationBell />}
          <div className="hidden lg:flex items-center gap-2"><AuthButtons /></div>
          <Sheet>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2" data-testid="mobile-menu-btn" aria-label="Menu"><Menu /></button>
            </SheetTrigger>
            <SheetContent side="right" className="flex flex-col gap-6 pt-12" data-testid="mobile-menu">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex flex-col gap-5"><NavLinks /></div>
              <LangToggle />
              <div className="flex flex-col items-start gap-3 border-t pt-6"><AuthButtons /></div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
